/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { dateUtils } from '@opengeoweb/shared';
import type { MapLocation } from '@opengeoweb/webmap-react';
import { EARTH_MEAN_RADIUS, MAX_PROXIMITY_RADIUS } from './Soundings/constants';
import type { FeaturesFromBE } from '../api/edr-api/types';

export const getDisplayValue = (
  instanceId: string,
  stringFormat = 'ISO',
): string => {
  const instanceAsDate = dateUtils.isoStringToDate(instanceId);
  if (instanceAsDate) {
    return stringFormat === 'ISO'
      ? dateUtils.dateToIsoString(instanceAsDate)!
      : dateUtils.dateToString(instanceAsDate, stringFormat, true)!;
  }
  return instanceId;
};

export const degToRad = (deg: number): number => (deg * Math.PI) / 180;
export const kmToRad = (km: number): number => km / EARTH_MEAN_RADIUS;

/**
 * Find nearest point to the target location in the given features, if it is within the specified maximum distance.
 * Uses (mid-latitude modified) equirectangular distance approximation which is not accurate for long
 * distances, but works for finding nearby (< 100 km) locations.
 *
 * @param targetLocation target location the user has clicked
 * @param pointFeatures features to search through
 * @param maxDistanceKm maximum distance to consider
 * @returns feature nearest to the target location or null if none are within the maximum distance
 */
export const findNearestLocationFeature = (
  pointFeatures: FeaturesFromBE[],
  targetLocation: [number, number],
  maxDistanceKm = MAX_PROXIMITY_RADIUS,
): FeaturesFromBE | null => {
  if (!pointFeatures.length) {
    return null;
  }

  // Convert max distance from km to radians and square it for comparison with squared distance.
  // Squared distance is used to avoid square root calculation in distance formula,
  // since we only need relative distances to find the nearest point.
  // Basically this is based on the fact that if distance d1 is less than distance d2, then d1^2 is also less than d2^2, so we can compare squared distances without losing correctness.
  const maxSphericalDistanceSquared = kmToRad(maxDistanceKm) ** 2;

  const targetLon = degToRad(targetLocation[0]);
  const targetLat = degToRad(targetLocation[1]);

  const nearestFeature =
    pointFeatures.reduce<{
      feature: FeaturesFromBE;
      sphericalDistanceSquared: number;
    } | null>((nearestResult, currentFeature) => {
      if (currentFeature.geometry.type !== 'Point') {
        return nearestResult;
      }

      const [featureLonDeg, featureLatDeg] = currentFeature.geometry
        .coordinates as [number, number];
      const featureLat = degToRad(featureLatDeg);
      const dLat = featureLat - targetLat;
      if (dLat * dLat > maxSphericalDistanceSquared) {
        return nearestResult;
      }

      const midLat = 0.5 * (targetLat + featureLat);
      const featureLon = degToRad(featureLonDeg);
      const dLon = featureLon - targetLon;
      const dLonConv = dLon * Math.cos(midLat);
      const sphericalDistanceSquared = dLonConv * dLonConv + dLat * dLat;

      if (sphericalDistanceSquared > maxSphericalDistanceSquared) {
        return nearestResult;
      }
      if (
        !nearestResult ||
        sphericalDistanceSquared < nearestResult.sphericalDistanceSquared
      ) {
        return {
          feature: currentFeature,
          sphericalDistanceSquared,
        };
      }
      return nearestResult;
    }, null)?.feature ?? null;

  return nearestFeature;
};

/**
 * Creates a circle polygon geometry centered at the given coordinates
 * @param center [lon, lat] coordinates
 * @param radiusKm radius in kilometers
 * @param numPoints number of points to approximate the circle
 * @returns GeoJSON Polygon coordinates
 */
export const createCirclePolygon = (
  center: [number, number],
  radiusKm: number,
  numPoints = 64,
): [number, number][][] => {
  const [lon, lat] = center;
  const radiusRad = radiusKm / EARTH_MEAN_RADIUS;
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;

  const points: [number, number][] = [];
  for (let i = 0; i <= numPoints; i += 1) {
    const angle = (i / numPoints) * 2 * Math.PI;
    const pointLatRad = Math.asin(
      Math.sin(latRad) * Math.cos(radiusRad) +
        Math.cos(latRad) * Math.sin(radiusRad) * Math.cos(angle),
    );
    const pointLonRad =
      lonRad +
      Math.atan2(
        Math.sin(angle) * Math.sin(radiusRad) * Math.cos(latRad),
        Math.cos(radiusRad) - Math.sin(latRad) * Math.sin(pointLatRad),
      );
    points.push([(pointLonRad * 180) / Math.PI, (pointLatRad * 180) / Math.PI]);
  }
  return [points] as [number, number][][];
};

/**
 * Creates a search circle feature for the given location
 * @param location MapLocation object containing lon and lat
 * @returns GeoJSON Feature or null if location is invalid
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export const searchCircleFeature = (location: MapLocation | null) =>
  location?.lon && location?.lat
    ? {
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: createCirclePolygon(
            [location.lon, location.lat],
            MAX_PROXIMITY_RADIUS,
          ),
        },
        id: 'search-radius-circle',
        properties: {
          name: 'Search Radius',
          fill: '#0fe644',
          'fill-opacity': 0.15,
          stroke: '#27a150',
          'stroke-width': 1.5,
          'stroke-opacity': 0.7,
          'stroke-dasharray': [1, 5],
          showUntilHoverOut: true,
        },
      }
    : null;
